import { useEffect, useState } from 'react';
import * as Y from 'yjs';
import { Input } from '@/components/ui/input';

/**
 * A controlled register cell bound to a single entry-level Y.Map<cellKey, value>.
 *
 * Local edits write into the Y.Map (which the relay broadcasts to peers + then
 * projects to register_entries via T-0032's projection seam). Peer edits land
 * via the Y.Map's observe callback so the input value stays in sync without
 * a re-render storm.
 *
 * Initial value is sourced from the live RegisterEntry row (the REST GET that
 * populated the table) so a fresh client sees the persisted state even before
 * the Y.Doc has finished its sync handshake — Yjs's sync step1 will resolve
 * any mismatch when it lands.
 */
export interface YjsCellProps {
  doc: Y.Doc;
  entryId: string;
  cellKey: string;
  initialValue: string;
  /** Optional callback when the local user changes the value. */
  onChange?: (value: string) => void;
  /** Match the existing input styles. */
  className?: string;
  placeholder?: string;
}

export function YjsCell({
  doc,
  entryId,
  cellKey,
  initialValue,
  onChange,
  className,
  placeholder,
}: YjsCellProps): JSX.Element {
  // Mirror the Y.Map value into local state so the input is a controlled
  // component. The Y.Map observer drives updates when peers edit.
  const [value, setValue] = useState<string>(initialValue);

  useEffect(() => {
    const entries = doc.getMap('entries') as Y.Map<unknown>;
    let cellMap = entries.get(entryId);
    if (!(cellMap instanceof Y.Map)) {
      cellMap = new Y.Map();
      entries.set(entryId, cellMap);
    }
    // Seed the initial value if the cell hasn't been touched yet.
    const existing = (cellMap as Y.Map<unknown>).get(cellKey);
    if (existing === undefined && initialValue !== '') {
      (cellMap as Y.Map<unknown>).set(cellKey, initialValue);
    } else if (typeof existing === 'string') {
      setValue(existing);
    }

    const onCellChange = (event: Y.YMapEvent<unknown>): void => {
      if (event.target !== cellMap) return;
      if (event.keysChanged.has(cellKey)) {
        const next = (cellMap as Y.Map<unknown>).get(cellKey);
        setValue(typeof next === 'string' ? next : '');
      }
    };
    (cellMap as Y.Map<unknown>).observe(onCellChange);
    return () => {
      (cellMap as Y.Map<unknown>).unobserve(onCellChange);
    };
  }, [doc, entryId, cellKey, initialValue]);

  const handleLocalChange = (next: string): void => {
    setValue(next);
    const entries = doc.getMap('entries') as Y.Map<unknown>;
    let cellMap = entries.get(entryId);
    if (!(cellMap instanceof Y.Map)) {
      cellMap = new Y.Map();
      entries.set(entryId, cellMap);
    }
    (cellMap as Y.Map<unknown>).set(cellKey, next);
    if (onChange) onChange(next);
  };

  return (
    <Input
      value={value}
      onChange={(e) => handleLocalChange(e.target.value)}
      className={className}
      placeholder={placeholder}
    />
  );
}
